/**
 * AJ 2026-10-01: "how do we speed up loading, and pages going white when you
 * click through quickly."
 *
 * WHAT THIS IS. One queue in front of every database read the dashboard
 * makes (the Supabase client's fetch, lib/supabase.ts). Three rules:
 *
 *   1. A CEILING. At most `maxConcurrent` reads are in flight for the page
 *      the reader is on. Each report already limits its own reads; nothing
 *      limited the sum. Clicking through the eleven tabs of Delta's dashboard
 *      quickly put about 1,100 reads on the database at once (measured on
 *      production, 2026-10-01): the database has two cores and an 8 second
 *      statement timeout, so twenty of those reads were cancelled (57014)
 *      and Route Profitability showed "Could not load the route data".
 *
 *   2. THE PAGE ON SCREEN GOES FIRST. A read belongs to the page that started
 *      it. When the reader moves on, that page's remaining reads keep going
 *      (they fill its cache, so coming back is instant) but only in slots the
 *      page on screen is not using.
 *
 *   3. ONE MORE TRY. A read the database cancelled for running out of time,
 *      or that failed in transit (502, 503, 504, a dropped connection), is
 *      asked again after a short pause before the page is told it failed. A
 *      read is a GET: asking twice changes nothing.
 *
 * WHAT IT IS NOT. It never changes what is asked or what comes back: same
 * URL, same headers, same response object. Writes, sign-in, storage and
 * function calls do not pass through the queue at all.
 *
 * WHO OWNS A READ. Reports page through their data with small worker pools,
 * so most reads are issued from the answer of an earlier one, by which time
 * the reader may be on another page. The owner is therefore remembered per
 * read SHAPE (table, select list, the names of the filters): a read whose
 * shape is still in progress for another page is a continuation of that
 * page's work and stays with it. A wrong guess only costs priority, never
 * correctness.
 *
 * NO STALL. A slot is given back when its read settles, and in any case
 * after SLOT_HOLD_MS: a connection that hangs can slow one read, never the
 * whole dashboard.
 *
 * Plain .mjs, no DOM and no Supabase (the clock, the timers, the fetch and
 * the "page on screen" are passed in), so node:test drives every rule
 * (scripts/request-queue.test.mjs).
 */

/** Reads in flight at once for the page on screen. */
export const DEFAULT_MAX_CONCURRENT = 8;

/** A slot is released after this long even if its read has not settled. */
export const SLOT_HOLD_MS = 30_000;

/** A read issued this soon after its shape last settled continues that chain. */
export const CHAIN_GRACE_MS = 400;

/** One retry for a statement timeout; two for a failure in transit. */
export const RETRY_LIMITS = Object.freeze({ timeout: 1, transient: 2 });

/** Pause before retry n (1-based), before jitter. */
export const RETRY_BASE_MS = 300;

/**
 * The shape of a read, or null when the request is not one the queue handles
 * (a write, sign-in, storage, a function call, another host's URL).
 *
 * Shape = path + the select and order lists + each filter's column and
 * operator (never its value) + whether it is paged. Two pages of one read,
 * or two months of one grouped read, share a shape; two reports reading the
 * same table differently do not.
 */
export function requestSignature(method, url) {
  const m = String(method || "GET").toUpperCase();
  if (m !== "GET" && m !== "HEAD") return null;
  let u;
  try {
    u = new URL(String(url));
  } catch {
    return null;
  }
  if (!u.pathname.startsWith("/rest/v1/") || u.pathname.startsWith("/rest/v1/rpc/")) return null;
  const parts = [];
  for (const [name, value] of u.searchParams) {
    if (name === "select" || name === "order") parts.push(`${name}=${value}`);
    else if (name === "offset" || name === "limit") parts.push(name);
    else parts.push(`${name}=${String(value).split(".")[0]}`);
  }
  parts.sort();
  return `${m} ${u.pathname}?${parts.join("&")}`;
}

function abortError(signal) {
  if (signal && signal.reason !== undefined) return signal.reason;
  const err = new Error("The operation was aborted.");
  err.name = "AbortError";
  return err;
}

/**
 * The queue. `enqueue(run, { shape, signal })` resolves or rejects with
 * whatever `run()` does.
 *
 * @param {{
 *   maxConcurrent?: number,
 *   currentOwner?: () => string,
 *   now?: () => number,
 *   setTimer?: (fn: () => void, ms: number) => unknown,
 *   clearTimer?: (handle: unknown) => void,
 *   slotHoldMs?: number,
 *   chainGraceMs?: number,
 * }} [opts]
 */
export function createRequestQueue(opts = {}) {
  const maxConcurrent = Math.max(
    1,
    Math.trunc(Number(opts.maxConcurrent) || DEFAULT_MAX_CONCURRENT),
  );
  const currentOwner = typeof opts.currentOwner === "function" ? opts.currentOwner : () => "";
  const now = typeof opts.now === "function" ? opts.now : () => Date.now();
  const setTimer =
    typeof opts.setTimer === "function" ? opts.setTimer : (fn, ms) => setTimeout(fn, ms);
  const clearTimer =
    typeof opts.clearTimer === "function" ? opts.clearTimer : (handle) => clearTimeout(handle);
  const slotHoldMs = Number(opts.slotHoldMs) > 0 ? Number(opts.slotHoldMs) : SLOT_HOLD_MS;
  const chainGraceMs = Number(opts.chainGraceMs) >= 0 ? Number(opts.chainGraceMs) : CHAIN_GRACE_MS;

  /** Waiting tasks, oldest first. */
  const waiting = [];
  /** Tasks holding a slot. */
  const holding = new Set();
  /** shape -> { owner, pending, lastSettled } */
  const chains = new Map();
  const stats = { started: 0, settled: 0, maxInFlight: 0, released: 0 };

  function ownerFor(shape) {
    const here = currentOwner();
    if (!shape) return { owner: here, chain: null };
    let chain = chains.get(shape);
    const continuing =
      chain &&
      chain.owner !== here &&
      (chain.pending > 0 || now() - chain.lastSettled < chainGraceMs);
    if (!chain) {
      chain = { owner: here, pending: 0, lastSettled: -Infinity };
      chains.set(shape, chain);
    } else if (!continuing) {
      chain.owner = here;
    }
    chain.pending += 1;
    return { owner: chain.owner, chain };
  }

  function release(task) {
    if (!holding.delete(task)) return;
    if (task.timer !== null) clearTimer(task.timer);
    task.timer = null;
    pump();
  }

  function start(task) {
    holding.add(task);
    stats.started += 1;
    stats.maxInFlight = Math.max(stats.maxInFlight, holding.size);
    // The safety valve: a read that never settles gives its slot back.
    task.timer = setTimer(() => {
      task.timer = null;
      if (holding.has(task)) {
        stats.released += 1;
        release(task);
      }
    }, slotHoldMs);
    let result;
    try {
      result = Promise.resolve(task.run());
    } catch (err) {
      result = Promise.reject(err);
    }
    result.then(
      (value) => finish(task, () => task.resolve(value)),
      (err) => finish(task, () => task.reject(err)),
    );
  }

  function finish(task, settle) {
    stats.settled += 1;
    if (task.chain) {
      task.chain.pending = Math.max(0, task.chain.pending - 1);
      task.chain.lastSettled = now();
    }
    if (task.signal && task.onAbort) task.signal.removeEventListener("abort", task.onAbort);
    settle();
    release(task);
  }

  function pump() {
    for (;;) {
      if (waiting.length === 0) return;
      const here = currentOwner();
      let front = 0;
      for (const t of holding) if (t.owner === here) front += 1;
      const index = waiting.findIndex((t) => t.owner === here);
      if (index >= 0) {
        // The page on screen: its own ceiling, whatever else is in flight.
        if (front >= maxConcurrent) return;
        start(waiting.splice(index, 1)[0]);
        continue;
      }
      // Another page's read: only in a slot nobody is using.
      if (holding.size >= maxConcurrent) return;
      start(waiting.shift());
    }
  }

  function enqueue(run, meta = {}) {
    const signal = meta.signal ?? null;
    if (signal && signal.aborted) return Promise.reject(abortError(signal));
    return new Promise((resolve, reject) => {
      const { owner, chain } = ownerFor(meta.shape ?? null);
      const task = { run, owner, chain, resolve, reject, signal, onAbort: null, timer: null };
      if (signal) {
        // Aborted while still waiting: never sent, rejected as fetch would.
        task.onAbort = () => {
          const at = waiting.indexOf(task);
          if (at < 0) return;
          waiting.splice(at, 1);
          if (chain) chain.pending = Math.max(0, chain.pending - 1);
          reject(abortError(signal));
        };
        signal.addEventListener("abort", task.onAbort, { once: true });
      }
      waiting.push(task);
      pump();
    });
  }

  return {
    enqueue,
    /** Re-evaluate who goes next (the page on screen changed). */
    pump,
    get inFlight() {
      return holding.size;
    },
    get queued() {
      return waiting.length;
    },
    stats,
  };
}

/** A retryable answer, or null. */
async function retryKind(response, error, signal) {
  if (error !== undefined) {
    if (signal && signal.aborted) return null;
    const name = error && typeof error === "object" ? error.name : "";
    if (name === "AbortError" || name === "TimeoutError") return null;
    return "transient";
  }
  if (!response) return null;
  if (response.status === 502 || response.status === 503 || response.status === 504) {
    return "transient";
  }
  if (response.status === 500) {
    try {
      const body = await response.clone().text();
      // Postgres 57014: "canceling statement due to statement timeout".
      if (/"57014"/.test(body) || /statement timeout/i.test(body)) return "timeout";
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * A fetch that sends database reads through the queue and gives a failed
 * read its retry. Everything else goes straight to `baseFetch`.
 *
 * @param {{
 *   baseFetch: typeof fetch,
 *   queue: ReturnType<typeof createRequestQueue>,
 *   sleep?: (ms: number) => Promise<void>,
 *   random?: () => number,
 *   onRetry?: (info: { kind: string, attempt: number, shape: string }) => void,
 * }} deps
 */
export function createQueuedFetch(deps) {
  const { baseFetch, queue } = deps;
  const sleep =
    typeof deps.sleep === "function"
      ? deps.sleep
      : (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const random = typeof deps.random === "function" ? deps.random : Math.random;
  const onRetry = typeof deps.onRetry === "function" ? deps.onRetry : () => {};

  return async function queuedFetch(input, init) {
    const isRequest = typeof input === "object" && input !== null && "url" in input;
    const url = isRequest ? input.url : String(input);
    const method = (init && init.method) || (isRequest ? input.method : "GET");
    const shape = requestSignature(method, url);
    if (shape === null) return baseFetch(input, init);
    const signal = (init && init.signal) || (isRequest ? input.signal : null) || null;
    const used = { timeout: 0, transient: 0 };
    for (let attempt = 1; ; attempt += 1) {
      let response;
      let error;
      try {
        response = await queue.enqueue(() => baseFetch(input, init), { shape, signal });
      } catch (err) {
        error = err === undefined ? new Error("request failed") : err;
      }
      const kind = await retryKind(response, error, signal);
      if (kind === null || used[kind] >= RETRY_LIMITS[kind]) {
        if (error !== undefined) throw error;
        return response;
      }
      used[kind] += 1;
      onRetry({ kind, attempt, shape });
      // The failed answer is dropped: free its connection before asking again.
      try {
        if (response && response.body && typeof response.body.cancel === "function") {
          await response.body.cancel();
        }
      } catch {
        // Already consumed or closed.
      }
      await sleep(RETRY_BASE_MS * 2 ** (attempt - 1) + Math.floor(random() * 200));
      if (signal && signal.aborted) {
        if (error !== undefined) throw error;
        return response;
      }
    }
  };
}
